import { useEffect } from 'react';
import { gsap } from 'gsap';

/**
 * Scroll rate in pixels per second. Duration is derived from this and the
 * measured content width, so the marquee moves at the same visual speed
 * regardless of how much text it holds or how wide the viewport is.
 *
 * The previous fixed `duration: 20` worked out at ~63px/s on a desktop
 * viewport, so 36px/s is a little over half that. This is the one number
 * to tune if it needs to be faster or slower.
 */
const MARQUEE_SPEED = 36;

/**
 * Marquee animation hook for horizontal scrolling text effect.
 * Creates seamless infinite scroll animation with performance optimization.
 */
export const useMarqueeAnimation = () => {
  useEffect(() => {
    const marquee = document.querySelector('.marquee_container');
    if (!marquee) return;

    const marqueeContent = marquee.querySelector('.marquee_content');
    if (!marqueeContent) return;

    const marqueeContentClone = marqueeContent.cloneNode(true);
    marquee.append(marqueeContentClone);

    let tween;
    let resizeTimer;

    // Distance from one copy's left edge to the next copy's left edge. Using
    // offsetLeft rather than computed width accounts for the margin and gap
    // between the copies, which is what keeps the loop seam from jumping.
    const measureDistance = () =>
      marqueeContentClone.offsetLeft - marqueeContent.offsetLeft;

    const playMarquee = () => {
      const progress = tween ? tween.progress() : 0;
      tween && tween.progress(0).kill();

      const distance = measureDistance();
      if (!distance) return;

      tween = gsap.fromTo(marquee.children,
        { x: 0 },
        {
          x: -distance,
          duration: distance / MARQUEE_SPEED,
          repeat: -1,
          ease: 'none',
          force3D: true
        }
      );
      tween.progress(progress);
    };

    // Re-measure on resize so the speed stays constant as the font scales.
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(playMarquee, 150);
    };

    playMarquee();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
      if (tween) tween.kill();
      marqueeContentClone.remove();
    };
  }, []);
};
